import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import RecentReportsTab from "./RecentReportsTab";
import SchedulesTab from "./SchedulesTab";

type TabKey = "recent" | "schedules";

interface LastRefresh {
  finished_at: string | null;
  status: string | null;
  trigger_type: string | null;
  rows_loaded_total?: number | null;
}

function formatNL(iso: string): string {
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function relativeNL(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "zojuist";
  if (min < 60) return `${min} min geleden`;
  const uur = Math.round(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dag = Math.round(uur / 24);
  return `${dag} ${dag === 1 ? "dag" : "dagen"} geleden`;
}

function LastRefreshIndicator() {
  const q = useQuery({
    queryKey: ["etl-last-refresh"],
    queryFn: async () => (await api.get<LastRefresh>("/etl/last-refresh")).data,
    refetchInterval: 60_000,
  });

  if (q.isLoading || q.isError || !q.data?.finished_at) return null;

  const { finished_at, status } = q.data;
  const ok = status === "SUCCESS";
  const dotColor = ok ? "bg-emerald-500" : "bg-amber-500";

  return (
    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
      <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} aria-hidden />
      <span>
        Data laatst bijgewerkt: {formatNL(finished_at)}{" "}
        <span className="text-slate-400">({relativeNL(finished_at)})</span>
      </span>
      {!ok && status && (
        <span className="font-medium text-amber-600">— laatste refresh: {status}</span>
      )}
    </p>
  );
}

export default function DashboardPage() {
  const [tab, setTab] = useState<TabKey>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "schedules" ? "schedules" : "recent";
  });

  const switchTab = (next: TabKey) => {
    setTab(next);
    const url = new URL(window.location.href);
    if (next === "recent") url.searchParams.delete("tab");
    else url.searchParams.set("tab", next);
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-kbs-primary">Dashboard</h1>
          <LastRefreshIndicator />
        </div>
        <Link
          to="/genereer"
          className="rounded bg-kbs-primary px-4 py-2 text-sm font-semibold text-white hover:bg-kbs-dark"
        >
          + Nieuw rapport
        </Link>
      </div>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <TabButton active={tab === "recent"} onClick={() => switchTab("recent")}>
            Recente rapporten
          </TabButton>
          <TabButton active={tab === "schedules"} onClick={() => switchTab("schedules")}>
            Geplande rapporten
          </TabButton>
        </nav>
      </div>

      {tab === "recent" ? <RecentReportsTab /> : <SchedulesTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "border-b-2 px-1 pb-3 text-sm font-medium transition " +
        (active
          ? "border-kbs-primary text-kbs-primary"
          : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700")
      }
    >
      {children}
    </button>
  );
}
